/**
 * NOTE on permissions: mobile used react-native-permissions to read a
 * persistent "BLOCKED" (never-ask-again) location permission state. The
 * browser Permissions API has a similar concept (navigator.permissions.query)
 * but support and behavior vary; this uses it where available and falls back
 * to treating any geolocation error as "permission not granted" without
 * distinguishing the permanently-blocked case.
 */
import React, { useCallback, useEffect, useState } from 'react';

import CSSModule from 'react-css-modules';
import { MapPin } from 'react-feather';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { BusinessInfoTile, Spinner } from 'components';
import { actions } from 'coupon-common/src/modules/businesses/recommended';

import { RouteNames } from '../../../constants';
import styles from './BusinessesListRecommended.module.scss';
import messages from './messages';

const BusinessesListRecommended = () => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const recommendedState = useSelector(state => state.businesses.recommended);
  const { fetching, fetched, data, error } = recommendedState;
  const { results: list, nextPage } = data || { results: [] };

  const [permissionState, setPermissionState] = useState('unknown'); // 'granted' | 'denied' | 'unknown'

  useEffect(() => {
    if (navigator.permissions?.query) {
      navigator.permissions
        .query({ name: 'geolocation' })
        .then(status => {
          setPermissionState(status.state === 'granted' ? 'granted' : status.state === 'denied' ? 'denied' : 'unknown');
          status.onchange = () => {
            setPermissionState(status.state === 'granted' ? 'granted' : status.state === 'denied' ? 'denied' : 'unknown');
          };
        })
        .catch(() => setPermissionState('unknown'));
    }
  }, []);

  const getRecommendedList = useCallback(
    params => {
      dispatch(actions.getRecommendedList(params));
    },
    [dispatch],
  );

  useEffect(() => {
    getRecommendedList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const requestLocationAccess = () => {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      () => {
        setPermissionState('granted');
        getRecommendedList();
      },
      () => setPermissionState('denied'),
    );
  };

  const goToDetails = id => navigate(`${RouteNames.BUSINESSES_DETAILS}/${id}`);

  const handleLoadMore = () => {
    if (nextPage) {
      getRecommendedList({ page: nextPage });
    }
  };

  const permissionLikelyDenied = !!error && permissionState === 'denied';

  if (permissionLikelyDenied) {
    return (
      <div styleName="page">
        <div styleName="permission-card">
          <div styleName="permission-icon">
            <MapPin size={36} />
          </div>
          <p styleName="permission-text">{intl.formatMessage(messages.allowAccessToLocationMessage)}</p>
          <button type="button" styleName="permission-btn" onClick={requestLocationAccess}>
            Allow Location Access
          </button>
        </div>
      </div>
    );
  }

  return (
    <div styleName="page">
      <div styleName="wrap">
        {error && !permissionLikelyDenied && <p styleName="state-message">Something went wrong loading recommendations.</p>}

        {!error && !fetched && fetching && (
          <div styleName="state-message">
            <Spinner show size="md" />
          </div>
        )}

        {!error && list && list.length === 0 && fetched && (
          <p styleName="state-message">{intl.formatMessage(messages.noRecommendedMessage)}</p>
        )}

        {!error && list && list.length > 0 && (
          <>
            <div styleName="list">
              {list.map((item, index) => (
                <BusinessInfoTile key={item.id} first={index === 0} data={item} onPress={() => goToDetails(item.id)} />
              ))}
            </div>
            {nextPage && (
              <div styleName="load-more-row">
                <button type="button" styleName="load-more-btn" onClick={handleLoadMore} disabled={fetching}>
                  {fetching ? <Spinner show size="sm" /> : 'Load more'}
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default CSSModule(BusinessesListRecommended, styles, { allowMultiple: true });
